/**
 * « Préparer l'année » d'UN site (2026-10-30) : ses contrats d'entretien en tête (plusieurs par site depuis le 2026-11-03), puis
 * par machine les visites dues, atteintes par les heures et faites, et ses lignes ; les visites de contrôle de chaque contrat ;
 * les échéances réglementaires du site. On coche, on
 * crée : chaque ligne devient une intervention « à affecter ». Une visite due que les heures n'atteignent pas attend un choix
 * (« avancer le prochain palier » ou « visite sans palier »), jamais présélectionné.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useDroitsPlanning } from '../auth/useDroits';
import { ApiError, api, type BilanCreationSite, type ContratsDuSite, type ContratVue, type NatureBon, type PreparationSite } from '../api/client';
import { jourFr } from '../domaine/planning';
import { phraseFenetre, phraseRaison, phraseRetard } from '../domaine/preparer-annee';
import {
  corpsCreationSite,
  nomMachineSite,
  peutCreerSite,
  phraseAnnee,
  phraseCompteurs,
  phraseContrat,
  phraseMachines,
  phraseDecalage,
  phrasePalierSite,
  phraseQuandSite,
  phraseRegime,
  phraseRetardSite,
  type ChoixVisiteDue,
} from '../domaine/preparer-site';
import { ContratEntretienPanneau, TerminerContratPanneau } from './ContratEntretienPanneau';
import { ErreurFormulaire } from './Formulaire';

const basculer = (s: ReadonlySet<string>, id: string): ReadonlySet<string> => {
  const suite = new Set(s);
  if (suite.has(id)) suite.delete(id);
  else suite.add(id);
  return suite;
};

export function PreparerSite({ siteId, natures }: { siteId: string; natures: readonly NatureBon[] }) {
  const droits = useDroitsPlanning();
  const [decalage, setDecalage] = useState<0 | 1>(0);
  const [vue, setVue] = useState<PreparationSite | null>(null);
  const [contrat, setContrat] = useState<ContratsDuSite | null>(null);
  const [erreur, setErreur] = useState<Error | null>(null);
  const [entretiens, setEntretiens] = useState<ReadonlySet<string>>(new Set());
  const [choix, setChoix] = useState<ReadonlyMap<string, ChoixVisiteDue>>(new Map());
  const [controles, setControles] = useState<ReadonlySet<string>>(new Set());
  const [echeances, setEcheances] = useState<ReadonlySet<string>>(new Set());
  const [natureId, setNatureId] = useState('');
  const [natureControleId, setNatureControleId] = useState('');
  const [panneau, setPanneau] = useState<{ readonly genre: 'modifier' | 'terminer'; readonly contrat: ContratVue | null } | null>(null);
  const [historique, setHistorique] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [bilan, setBilan] = useState<BilanCreationSite | null>(null);

  const charger = useCallback(async () => {
    try {
      const [v, c] = await Promise.all([api.preparerSite(siteId, decalage), api.contratsDuSite(siteId)]);
      setVue(v);
      setContrat(c);
      // Rien n'est coché au chargement (Benjamin, 2026-10-31) : c'est lui qui choisit.
      setEntretiens(new Set());
      setChoix(new Map());
      setControles(new Set());
      setEcheances(new Set());
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Préparation illisible.'));
    }
  }, [siteId, decalage]);

  useEffect(() => {
    setVue(null);
    setBilan(null);
    void charger();
  }, [charger]);

  if (!vue || !contrat) {
    return (
      <>
        <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />
        {!erreur && <div className="vide">Calcul des propositions du site…</div>}
      </>
    );
  }

  const etat = { vue, natureId, natureControleId, entretiens, choix, controles, echeances };
  const lecture = peutCreerSite(etat);
  const total = entretiens.size + controles.size + echeances.size;

  const creer = async () => {
    if (!droits.peutGererPlanning || !lecture.ok) return;
    if (!window.confirm(`Créer ${total === 1 ? 'une intervention' : `${total} interventions`} « à affecter » pour ce site ?`)) return;
    setEnCours(true);
    try {
      const r = await api.creerDepuisSite(siteId, corpsCreationSite(etat));
      // Relire AVANT d'annoncer : la relecture remet l'erreur à zéro, le bilan se pose ensuite.
      await charger();
      setBilan(r);
    } catch (e) {
      setErreur(e instanceof Error ? e : new Error('Création refusée.'));
    } finally {
      setEnCours(false);
    }
  };

  const choisir = (id: string, c: ChoixVisiteDue) => {
    setChoix((m) => new Map(m).set(id, c));
    setEntretiens((s) => new Set(s).add(id));
  };

  return (
    <>
      <ErreurFormulaire message={erreur?.message ?? null} details={erreur instanceof ApiError ? erreur.details : undefined} />

      <div className="carte contrat-site">
        <div className="carte-corps">
          <h2>
            {vue.site.client ? `${vue.site.client} · ` : ''}
            {vue.site.nom}
            {vue.site.ville ? ` (${vue.site.ville})` : ''}
          </h2>
          <div className="saisie-ligne">
            <span className="aide">Année :</span>
            <button type="button" className={`chip${decalage === 0 ? ' actif' : ''}`} aria-pressed={decalage === 0} disabled={enCours} onClick={() => setDecalage(0)}>
              En cours
            </button>
            <button type="button" className={`chip${decalage === 1 ? ' actif' : ''}`} aria-pressed={decalage === 1} disabled={enCours} onClick={() => setDecalage(1)}>
              Suivante
            </button>
            <span className="aide">Chaque contrat suit sa propre année, d’anniversaire en anniversaire ; hors contrat : {phraseAnnee(vue.annee)}.</span>
          </div>
          <div className="saisie-ligne">
            <strong>
              {vue.contrats.length === 0
                ? 'Aucun contrat d’entretien en cours sur ce site'
                : `Contrats qui couvrent ce site (${vue.contrats.length})`}
            </strong>
            {droits.peutGererContrats && (
              <button type="button" className="secondaire" disabled={enCours} onClick={() => setPanneau({ genre: 'modifier', contrat: null })}>
                + Nouveau contrat
              </button>
            )}
          </div>
          {vue.contrats.length > 0 && (
            <div className="contrats-site">
              {vue.contrats.map((c) => {
                const complet = contrat.contrats.find((x) => x.id === c.id) ?? null;
                const autresSites = complet ? complet.machines.length - c.machinesDuSite : 0;
                return (
                  <div key={c.id} className="contrat-carte">
                    <h3>{c.label}</h3>
                    <div className="aide">{phraseContrat(c)}</div>
                    <div>
                      {phraseMachines(c.machinesDuSite)} sur ce site
                      {autresSites > 0 ? ` · ${phraseMachines(autresSites)} sur d’autres sites` : ''}
                    </div>
                    <div className="aide">Année {phraseAnnee(c.annee)}</div>
                    {droits.peutGererContrats && complet && (
                      <div className="saisie-ligne">
                        <button type="button" className="secondaire" disabled={enCours} onClick={() => setPanneau({ genre: 'modifier', contrat: complet })}>
                          Modifier
                        </button>
                        <button type="button" className="secondaire" disabled={enCours} onClick={() => setPanneau({ genre: 'terminer', contrat: complet })}>
                          Terminer le contrat
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {contrat.contrats.some((c) => c.termine) && (
            <>
              <button type="button" className="secondaire" onClick={() => setHistorique((v) => !v)}>
                {historique ? 'Masquer' : 'Voir'} les contrats terminés ({contrat.contrats.filter((c) => c.termine).length})
              </button>
              {historique && (
                <div className="contrats-site">
                  {contrat.contrats
                    .filter((c) => c.termine)
                    .map((c) => (
                      <div key={c.id} className="contrat-carte termine">
                        <h3>{c.label}</h3>
                        <div>
                          signé le {jourFr(c.signedOn)} · terminé le {c.endedOn ? jourFr(c.endedOn) : '—'}
                        </div>
                        <div>{phraseMachines(c.machines.length)} · historique, lecture seule</div>
                      </div>
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {bilan && (
        <div className="message" role="status">
          <strong>
            {bilan.creees.length} intervention{bilan.creees.length > 1 ? 's' : ''} créée{bilan.creees.length > 1 ? 's' : ''}
            {bilan.refusees.length > 0 ? `, ${bilan.refusees.length} refusée${bilan.refusees.length > 1 ? 's' : ''}` : ''}.
          </strong>{' '}
          {bilan.creees.length > 0 && <Link to="/planning/interventions">Les voir dans le planning (liste « À affecter »)</Link>}
          {bilan.refusees.length > 0 && (
            <ul>
              {bilan.refusees.map((r) => (
                <li key={r.id}>{r.raison}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <h2>Entretiens</h2>
      {vue.machines.length === 0 && <div className="vide">Ce site n’a aucune machine active.</div>}
      {vue.machines.map((m) => (
        <section key={m.machine.id} className="machine-preparee">
          <h3 className="preparer-site">
            <Link to={`/parc?asset=${m.machine.id}`}>{nomMachineSite(m.machine)}</Link>{' '}
            <span className="aide">
              {[m.machine.famille, m.machine.designationClient, m.machine.numeroSerie ? `n° ${m.machine.numeroSerie}` : null, m.gamme?.label].filter(Boolean).join(' · ')}
            </span>
          </h3>
          <div className="aide">
            {m.contrat ? <span className="badge-contrat">{m.contrat.label}</span> : null}{' '}
            {m.couverte
              ? `Sous contrat${m.heuresParAn !== null ? ` : ${m.heuresParAn.toLocaleString('fr-FR')} h/an` : ''} · base « ${m.base === 'CONTRACT' ? 'contrat' : 'relevés'} »`
              : 'Sans contrat'}
            {phraseCompteurs(m) && ` · ${phraseCompteurs(m)}`}
            {phraseRegime(m) && ` · dates ${phraseRegime(m)}`}
          </div>
          {m.ecart && (
            <div className="message attention">
              {m.ecart}{' '}
              {m.base === 'CONTRACT'
                ? 'Les dates suivent le contrat : vous pouvez passer sa base sur « relevés » dans le contrat.'
                : 'Les dates suivent les relevés : le nombre de visites dues reste celui du contrat.'}
            </div>
          )}
          {m.message && <div className="aide">{m.message}</div>}
          {m.lignes.length > 0 && (
            <div className="tableau-defilant">
              <table className="tableau-ecarts">
                <thead>
                  <tr>
                    <th>Créer</th>
                    <th>Palier</th>
                    <th>Quand</th>
                    <th>État</th>
                  </tr>
                </thead>
                <tbody>
                  {m.lignes.map((l) => (
                    <tr key={l.id}>
                      <td>
                        {l.etat === 'A_CREER' && l.genre !== 'DUE' && (
                          <input
                            type="checkbox"
                            aria-label={`Créer l’entretien de ${nomMachineSite(m.machine)}`}
                            checked={entretiens.has(l.id)}
                            disabled={enCours || !droits.peutGererPlanning}
                            onChange={() => setEntretiens((s) => basculer(s, l.id))}
                          />
                        )}
                        {l.etat === 'A_CREER' && l.genre === 'DUE' && (
                          <input
                            type="checkbox"
                            aria-label={`Créer la visite due de ${nomMachineSite(m.machine)}`}
                            checked={entretiens.has(l.id)}
                            disabled={enCours || !droits.peutGererPlanning}
                            onChange={() => setEntretiens((s) => basculer(s, l.id))}
                          />
                        )}
                      </td>
                      <td>{phrasePalierSite(l)}</td>
                      <td>
                        {phraseQuandSite(l)}
                        {phraseRetardSite(l) && <div className="aide">{phraseRetardSite(l)}</div>}
                      </td>
                      <td>
                        {l.etat === 'A_CREER' && l.genre === 'DUE' && (
                          <div role="radiogroup" aria-label="Que faire de cette visite due ?">
                            <div className="aide">{l.message}</div>
                            <label>
                              <input
                                type="radio"
                                name={`due-${l.id}`}
                                checked={choix.get(l.id) === 'AVANCER'}
                                disabled={enCours || !droits.peutGererPlanning || l.palierPropose === null}
                                onChange={() => choisir(l.id, 'AVANCER')}
                              />{' '}
                              Avancer le prochain palier{l.palierPropose ? ` (${l.palierPropose.code})` : ''}
                            </label>{' '}
                            <label>
                              <input
                                type="radio"
                                name={`due-${l.id}`}
                                checked={choix.get(l.id) === 'SANS_PALIER'}
                                disabled={enCours || !droits.peutGererPlanning}
                                onChange={() => choisir(l.id, 'SANS_PALIER')}
                              />{' '}
                              Visite sans palier
                            </label>
                          </div>
                        )}
                        {l.etat === 'A_CREER' && l.genre !== 'DUE' && (
                          <>
                            {l.fenetre && <span className="aide">Fenêtre {phraseFenetre(l.fenetre.debut, l.fenetre.fin)}</span>}
                            {l.message && <div className={l.auDelaDuContrat ? 'message attention' : 'aide'}>{l.message}</div>}
                          </>
                        )}
                        {l.etat === 'DEJA_PLANIFIEE' && l.intervention && (
                          <>
                            <Link to={`/planning/interventions?intervention=${l.intervention.id}`}>
                              Déjà planifié{l.intervention.numero ? ` : ${l.intervention.numero}` : ''}
                              {l.intervention.premierJour ? ` (le ${jourFr(l.intervention.premierJour)})` : ''}
                            </Link>
                            {phraseDecalage(l.intervention.decalageJours) && <div className="message attention">{phraseDecalage(l.intervention.decalageJours)}</div>}
                          </>
                        )}
                        {l.etat === 'A_COMPLETER' && <span className="message attention">{l.message ?? 'À compléter.'}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}

      {vue.controles.length > 0 && (
        <>
          <h2>Visites de contrôle des contrats</h2>
          <p className="aide">Une intervention par contrat et par période, sur les machines de ce contrat présentes sur ce site, avec leurs propres bons.</p>
          <div className="tableau-defilant">
            <table className="tableau-ecarts">
              <thead>
                <tr>
                  <th>Créer</th>
                  <th>Contrat</th>
                  <th>Période</th>
                  <th>État</th>
                </tr>
              </thead>
              <tbody>
                {vue.controles.map((c) => (
                  <tr key={c.id}>
                    <td>
                      {c.etat === 'A_CREER' && (
                        <input
                          type="checkbox"
                          aria-label={`Créer la visite de contrôle du ${jourFr(c.debut)}`}
                          checked={controles.has(c.id)}
                          disabled={enCours || !droits.peutGererPlanning}
                          onChange={() => setControles((s) => basculer(s, c.id))}
                        />
                      )}
                    </td>
                    <td>
                      {c.contratLibelle}
                      <div className="aide">{phraseMachines(c.machines)}</div>
                    </td>
                    <td>
                      du {jourFr(c.debut)} au {jourFr(c.fin)}
                    </td>
                    <td>
                      {c.etat === 'A_CREER' ? (
                        <span className="aide">Fenêtre {phraseFenetre(c.fenetre.debut, c.fenetre.fin)}</span>
                      ) : (
                        c.intervention && (
                          <Link to={`/planning/interventions?intervention=${c.intervention.id}`}>
                            Déjà planifiée{c.intervention.numero ? ` : ${c.intervention.numero}` : ''}
                          </Link>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2>Échéances réglementaires du site</h2>
      {vue.echeances.length === 0 ? (
        <div className="vide">Aucune échéance réglementaire d’ici la fin de l’année.</div>
      ) : (
        <div className="tableau-defilant">
          <table className="tableau-ecarts">
            <thead>
              <tr>
                <th>Créer</th>
                <th>Échéance</th>
                <th>Règle</th>
                <th>Machine</th>
                <th>État</th>
              </tr>
            </thead>
            <tbody>
              {vue.echeances.map((p) => (
                <tr key={p.obligationId}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Créer une intervention pour ${p.regle}`}
                      checked={echeances.has(p.obligationId)}
                      disabled={p.etat !== 'A_CREER' || enCours || !droits.peutGererPlanning}
                      onChange={() => setEcheances((s) => basculer(s, p.obligationId))}
                    />
                  </td>
                  <td>
                    {jourFr(p.echeance)}
                    <div className="aide">{phraseRetard(p.retardJours)}</div>
                  </td>
                  <td>
                    {p.regle}
                    <div className="aide">{p.jalon}</div>
                  </td>
                  <td>{p.machine.libelle ?? p.machine.modele ?? 'Machine'}</td>
                  <td>
                    {p.etat === 'A_CREER' && <span className="aide">Fenêtre {phraseFenetre(p.fenetre.debut, p.fenetre.fin)}</span>}
                    {p.etat === 'DEJA_PLANIFIEE' && p.intervention && (
                      <Link to={`/planning/interventions?intervention=${p.intervention.id}`}>
                        Déjà planifiée{p.intervention.numero ? ` : ${p.intervention.numero}` : ''}
                      </Link>
                    )}
                    {p.etat === 'A_COMPLETER' && <span className="message attention">{phraseRaison(p.raison)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="editeur-barre">
        <div className="saisie-ligne">
          <label>
            Nature des entretiens
            <select value={natureId} disabled={enCours || !droits.peutGererPlanning} onChange={(e) => setNatureId(e.target.value)}>
              <option value="">Choisir…</option>
              {natures.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.label}
                </option>
              ))}
            </select>
          </label>
          {vue.controles.length > 0 && (
            <label>
              Nature des visites de contrôle
              <select value={natureControleId} disabled={enCours || !droits.peutGererPlanning} onChange={(e) => setNatureControleId(e.target.value)}>
                <option value="">Choisir…</option>
                {natures.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="editeur-barre-actions">
          <span className="aide">
            {!droits.peutGererPlanning
              ? 'Lecture seule : créer des interventions demande le droit de gérer le planning.'
              : total === 0
                ? 'Aucune ligne cochée'
                : `${total} ligne${total > 1 ? 's' : ''} cochée${total > 1 ? 's' : ''}`}
          </span>
          <button type="button" disabled={enCours || !droits.peutGererPlanning || !lecture.ok} title={lecture.ok ? undefined : lecture.erreur} onClick={() => void creer()}>
            Créer {total === 1 ? 'l’intervention' : `les ${total} interventions`}
          </button>
        </div>
        {droits.peutGererPlanning && !lecture.ok && total > 0 && <div className="aide">{lecture.erreur}</div>}
      </div>

      {panneau?.genre === 'modifier' && (
        <ContratEntretienPanneau vue={contrat} contrat={panneau.contrat} aujourdhui={vue.aujourdhui} onFermer={() => setPanneau(null)} onEnregistre={charger} />
      )}
      {panneau?.genre === 'terminer' && panneau.contrat && (
        <TerminerContratPanneau vue={contrat} contrat={panneau.contrat} aujourdhui={vue.aujourdhui} onFermer={() => setPanneau(null)} onEnregistre={charger} />
      )}
    </>
  );
}
